// React Imports
import { Fragment, ReactElement, useEffect, useState } from "react";

// Style Imports
import "./add-edit-portfolio.scss";

// Third Party Libraries Imports
import TabPane from "antd/es/tabs/TabPane";
import TextArea from "antd/es/input/TextArea";
import { Button, Form, Select, Tabs, Upload } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

// Local Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import Label from "../../components/label/label.component";
import CustomInput from "../../components/custom-input/custom-input.component";
import ReactQuill from "react-quill";
import EditPortfolioService from "./edit-portfolio.service";
import ReactCountryFlag from "react-country-flag";
import { useDispatch } from "react-redux";
import { ADD_TO_BREADCRUMB, REMOVE_FROM_BREADCRUMB } from "../../store/actions";
import CustomModal from "src/components/custom-modal/custom-modal";
import PdfViewer from "src/components/pdf-viewer";
import toast from "react-hot-toast";

const { Option } = Select;
const { Dragger } = Upload;

const AddEditPortfolio: React.FC = (): ReactElement => {
  const editPortfolioService = new EditPortfolioService();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  // eslint-disable-next-line no-restricted-globals
  const queryParams = new URLSearchParams(location.search);
  const edit = queryParams.get("edit");
  const { portfolioId } = useParams();
  const values = Form.useWatch([], form);
  const [countriesData, setCountriesData] = useState<any>([]);
  const [submitLoginForm, setSubmitLoginForm] = useState(false);
  const [portfolioDetails, setPortfolioDetails] = useState<any>(null);
  const [openPdfViewerModal, setOpenPdfViewerModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<any>({});

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitLoginForm(true);
      },
      () => {
        setSubmitLoginForm(false);
      }
    );
  }, [values]);

  useEffect(() => {
    fetchCountriesData();
    if (portfolioId) {
      fetchPortfolioDetailsData();
    }

    return () => {
      dispatch({
        type: REMOVE_FROM_BREADCRUMB,
        payload: {
          breadcrumb: [
            {
              text: portfolioDetails?.nameEn,
            },
          ],
        },
      });
    };
  }, []);

  const fetchCountriesData = async (): Promise<void> => {
    const data = await editPortfolioService.getCountriesData(portfolioId);

    if (data !== undefined) {
      setCountriesData(data);
    }
  };

  const fetchPortfolioDetailsData = async (): Promise<void> => {
    const data = await editPortfolioService.getPortfolioDetails(portfolioId);

    if (data !== undefined) {
      dispatch({
        type: ADD_TO_BREADCRUMB,
        payload: {
          breadcrumb: [
            {
              text: data?.nameEn,
              type: "text",
            },
          ],
        },
      });
      setPortfolioDetails(data);
    }
  };

  const onSubmit = async (values: any) => {
    await editPortfolioService.submitForm(values, portfolioDetails?.id);
  };

  const beforeUpload = (file) => {
    const isImageOrPdf =
      file.type === "image/png" ||
      file.type === "image/gif" ||
      file.type === "image/jpg" ||
      file.type === "image/jpeg" ||
      file.type === "application/pdf";

    if (!isImageOrPdf) {
      toast.error(
        "Error: Only PDF and image files are allowed for upload. Please try again with a valid file format!"
      );
    }

    if (!isImageOrPdf) {
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  return (
    <section className="add-edit-portfolio-container flex flex-col">
      <BreadcrumbComponent />

      <div className="flex flex-col py-[2%] bg-white rounded-[20px]">
        <h3 className=" text-xl border-b-[1px] pt-[1] px-[2%] pb-4 border-black-200">
          {edit ? "Edit portfolio" : "Create a portfolio"}
        </h3>
        {(portfolioId && portfolioDetails?.id) || !portfolioId ? (
          <Form
            form={form}
            className="p-[2%] w-full"
            onFinish={onSubmit}
            initialValues={{
              nameEn: portfolioDetails?.nameEn,
              nameAr: portfolioDetails?.nameAr,
              minimumAmount: portfolioDetails?.minimumAmount,
              currencyId: portfolioDetails?.currencyId,
              agreementFileEn: portfolioDetails?.agreementFileEn,
              agreementFileAr: portfolioDetails?.agreementFileAr,
              formFile: portfolioDetails?.formFile,
              descriptionEn: portfolioDetails?.descriptionEn,
              descriptionAr: portfolioDetails?.descriptionAr,
              termsAndConditionsEn: portfolioDetails?.termsAndConditionsEn,
              termsAndConditionsAr: portfolioDetails?.termsAndConditionsAr,
              newAgreementTerms: portfolioDetails?.newAgreementTerms,
              newAgreementTermsAr: portfolioDetails?.newAgreementTermsAr,
            }}
          >
            <div className="flex-wrap flex justify-between w-full">
              <div className="flex flex-wrap justify-between w-full md:w-[70%]">
                <div className=" flex flex-col w-full md:w-[49%]">
                  <Label text="Portfolio name (English)" />
                  <Form.Item
                    name="nameEn"
                    rules={[
                      {
                        required: true,
                        message: "Please input portfolio name (English)!",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Enter portfolio name (english)" />
                  </Form.Item>
                </div>
                <div className="flex flex-col w-full md:w-[49%]">
                  <Label text="Portfolio name (Arabic)" />
                  <Form.Item
                    name="nameAr"
                    rules={[
                      {
                        required: true,
                        message: "Please input portfolio name (Arabic)!",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Enter portfolio name (Arabic)" />
                  </Form.Item>
                </div>
                <div className="flex flex-col w-full md:w-[49%]">
                  <Label text="Minimum amount" />
                  <Form.Item
                    name="minimumAmount"
                    rules={[
                      {
                        required: true,
                        message: "Please input minimum amount!",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Enter minimum amount" />
                  </Form.Item>
                </div>
                <div className="field flex flex-col w-full md:w-[49%]">
                  <Label text="Currency" />
                  <Form.Item
                    name="currencyId"
                    rules={[
                      {
                        required: true,
                        message: "Please select currency!",
                      },
                    ]}
                  >
                    <Select placeholder="Select currency">
                      {countriesData.map((country: any) => (
                        <Option value={country.id} key={country.nameEn}>
                          <div
                            className="flex align-center"
                            style={{ gap: "5px" }}
                          >
                            <ReactCountryFlag
                              style={{
                                fontSize: "24px",
                                marginInlineEnd: "8px",
                              }}
                              countryCode={country?.countryCode}
                              svg
                            />
                            <span>{country.currencyCode}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
              <div className="flex flex-col w-full md:w-[28%]">
                <Label text="Portfolio agreement (English)" />
                <Form.Item
                  name="agreementFileEn"
                  rules={[
                    {
                      required: true,
                      message: "Please upload the agreement english file file",
                    },
                  ]}
                >
                  <Dragger
                    beforeUpload={beforeUpload}
                    customRequest={() => false}
                    multiple={false}
                    accept="image/*,.pdf"
                  >
                    <p className="ant-upload-text">
                      Click or drag file to this area to upload
                    </p>
                  </Dragger>
                </Form.Item>
                {portfolioDetails?.agreementFileEn ? (
                  <span
                    className="underline cursor-pointer mb-3 text-blue-500"
                    onClick={() => {
                      setSelectedDocument(portfolioDetails?.agreementFileEn);
                      setOpenPdfViewerModal(true);
                    }}
                  >
                    English portfolio agreement Attachment
                  </span>
                ) : (
                  ""
                )}
              </div>
            </div>

            <div className="flex flex-wrap justify-between">
              <div className="tabs flex flex-wrap justify-between w-full md:w-[70%]">
                <div className="flex flex-col w-full">
                  <Label text="Description" />
                  <div className="flex flex-col">
                    <Tabs type="card">
                      <TabPane tab="English" key="1">
                        <Form.Item
                          name={"descriptionEn"}
                          rules={[
                            {
                              required: true,
                              message:
                                "Please input english portfolio description!",
                            },
                          ]}
                        >
                          <TextArea
                            rows={4}
                            placeholder="Enter English portfolio description"
                          />
                        </Form.Item>
                      </TabPane>
                      <TabPane tab="Arabic" key="2">
                        <Form.Item
                          name={"descriptionAr"}
                          rules={[
                            {
                              required: true,
                              message:
                                "Please input arabic portfolio description!",
                            },
                          ]}
                        >
                          <TextArea
                            rows={4}
                            placeholder="Enter English portfolio description"
                          />
                        </Form.Item>
                      </TabPane>
                    </Tabs>
                  </div>
                </div>
              </div>
              <div className="flex flex-col w-full md:w-[28%]">
                <Label text="Portfolio agreement (Arabic)" />
                <Form.Item
                  name="agreementFileAr"
                  rules={[
                    {
                      required: true,
                      message: "Please upload the agreement file arabic file",
                    },
                  ]}
                >
                  <Dragger
                    beforeUpload={beforeUpload}
                    customRequest={() => false}
                    multiple={false}
                    accept="image/*,.pdf"
                  >
                    <p className="ant-upload-text">
                      Click or drag file to this area to upload
                    </p>
                  </Dragger>
                </Form.Item>
                {portfolioDetails?.agreementFileAr ? (
                  <span
                    className="underline cursor-pointer mb-3 text-blue-500"
                    onClick={() => {
                      setSelectedDocument(portfolioDetails?.agreementFileEn);
                      setOpenPdfViewerModal(true);
                    }}
                  >
                    Arabic portfolio agreement Attachment
                  </span>
                ) : (
                  ""
                )}
              </div>
            </div>

            <div className="attributes-uploader tabs flex-wrap flex justify-between items-baseline">
              <div className="flex flex-col w-full md:w-[70%]">
                <div className="flex flex-col">
                  <div className="flex flex-col w-full">
                    <Label text="Portfolio terms & conditions" />
                    <div className="flex flex-col">
                      <Tabs type="card">
                        <TabPane tab="English" key="1">
                          <Form.Item
                            name="termsAndConditionsEn"
                            rules={[
                              {
                                required: true,
                                message:
                                  "Please input english terms and conditions!",
                              },
                            ]}
                          >
                            <ReactQuill theme="snow" />
                          </Form.Item>
                        </TabPane>
                        <TabPane tab="Arabic" key="2">
                          <Form.Item
                            name="termsAndConditionsAr"
                            rules={[
                              {
                                required: true,
                                message:
                                  "Please input arabic terms and conditions!",
                              },
                            ]}
                          >
                            <ReactQuill theme="snow" />
                          </Form.Item>
                        </TabPane>
                      </Tabs>
                    </div>
                  </div>
                  <div className="flex flex-col w-full">
                    <Label text="New portfolio terms & conditions" />
                    <div className="flex flex-col">
                      <Tabs type="card">
                        <TabPane tab="English" key="1">
                          <Form.Item
                            name="newAgreementTerms"
                            rules={[
                              {
                                required: true,
                                message:
                                  "Please input new english terms and conditions!",
                              },
                            ]}
                          >
                            <ReactQuill theme="snow" />
                          </Form.Item>
                        </TabPane>
                        <TabPane tab="Arabic" key="2">
                          <Form.Item
                            name="newAgreementTermsAr"
                            rules={[
                              {
                                required: true,
                                message:
                                  "Please input new arabic terms and conditions!",
                              },
                            ]}
                          >
                            <ReactQuill theme="snow" />
                          </Form.Item>
                        </TabPane>
                      </Tabs>
                    </div>
                  </div>
                </div>
                <div className="tabs flex flex-col justify-between w-full">
                  <Form.List
                    name="strategies"
                    initialValue={
                      portfolioDetails?.strategies?.length
                        ? portfolioDetails?.strategies
                        : []
                    }
                  >
                    {(fields: any = 1, { add, remove }, { errors }) => (
                      <Form.Item>
                        <div className="flex flex-col">
                          {fields.map((field: any, index: any) => (
                            <Fragment key={index}>
                              <div className="flex flex-wrap justify-between">
                                <div className="hidden">
                                  <Form.Item
                                    {...field}
                                    name={[field.name, "id"]}
                                    validateTrigger={["onChange", "onBlur"]}
                                    hidden
                                  >
                                    <CustomInput
                                      type="hidden"
                                      placeholder="Enter portfolio ID"
                                    />
                                  </Form.Item>
                                </div>
                                <div className="flex flex-col w-full md:w-[49%]">
                                  <Label text={"Strategy name (English)"} />
                                  <Form.Item
                                    {...field}
                                    name={[field.name, "nameEn"]}
                                    validateTrigger={["onChange", "onBlur"]}
                                    rules={[
                                      {
                                        required: true,
                                        whitespace: true,
                                        message: "Please input english name",
                                      },
                                    ]}
                                  >
                                    <CustomInput placeholder="Enter portfolio name (english)" />
                                  </Form.Item>
                                </div>
                                <div className="flex flex-col w-full md:w-[49%]">
                                  <Label text={"Strategy name (Arabic)"} />
                                  <Form.Item
                                    {...field}
                                    name={[field.name, "nameAr"]}
                                    validateTrigger={["onChange", "onBlur"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please input arabic name",
                                      },
                                    ]}
                                  >
                                    <CustomInput placeholder="Enter portfolio name (english)" />
                                  </Form.Item>
                                </div>
                                <div className="flex flex-col w-full">
                                  <Label text={"Portfolio aim & strategy"} />
                                  <Tabs type="card">
                                    <TabPane tab="English" key="1">
                                      <Form.Item
                                        {...field}
                                        name={[field.name, "descriptionEn"]}
                                        validateTrigger={["onChange", "onBlur"]}
                                        rules={[
                                          {
                                            required: true,
                                            message:
                                              "Please input english portfolio description!",
                                          },
                                        ]}
                                      >
                                        <TextArea
                                          rows={4}
                                          placeholder="Enter English portfolio description"
                                        />
                                      </Form.Item>
                                    </TabPane>
                                    <TabPane tab="Arabic" key="2">
                                      <div className="fields flex column">
                                        <Form.Item
                                          {...field}
                                          name={[field.name, "descriptionAr"]}
                                          validateTrigger={[
                                            "onChange",
                                            "onBlur",
                                          ]}
                                          rules={[
                                            {
                                              required: true,
                                              message:
                                                "Please input arabic portfolio description!",
                                            },
                                          ]}
                                        >
                                          <TextArea
                                            rows={4}
                                            placeholder="Enter Arabic portfolio description"
                                          />
                                        </Form.Item>
                                      </div>
                                    </TabPane>
                                  </Tabs>
                                </div>

                                <Button
                                  className="flex items-center border-0 text-red-500 bg-transparent mb-3 shadow-none p-0"
                                  onClick={() => remove(field.name)}
                                >
                                  <MinusCircleOutlined />
                                  Delete
                                </Button>
                              </div>
                            </Fragment>
                          ))}
                          <Form.Item>
                            <div className="flex justify-end">
                              <Button
                                onClick={() => add()}
                                className="border-0 text-blue-500 bg-transparent mb-3 shadow-none p-0 text-base"
                                icon={<PlusOutlined />}
                              >
                                Add strategy
                              </Button>
                            </div>
                            <Form.ErrorList errors={errors} />
                          </Form.Item>
                        </div>
                      </Form.Item>
                    )}
                  </Form.List>
                </div>
              </div>
              <div className="flex flex-col w-full md:w-[28%]">
                <div className="flex flex-col w-full">
                  <Label text="Portfolio form " />
                  <Form.Item
                    name="formFile"
                    rules={[
                      {
                        required: true,
                        message: "Please upload the file",
                      },
                    ]}
                  >
                    <Dragger
                      beforeUpload={beforeUpload}
                      customRequest={() => false}
                      multiple={false}
                      accept="image/*,.pdf"
                    >
                      <p className="ant-upload-text">
                        Click or drag file to this area to upload
                      </p>
                    </Dragger>
                  </Form.Item>
                  {portfolioDetails?.formFile ? (
                    <span
                      className="underline cursor-pointer mb-3 text-blue-500"
                      onClick={() => {
                        setSelectedDocument(portfolioDetails?.agreementFileEn);
                        setOpenPdfViewerModal(true);
                      }}
                    >
                      Portfolio Form Attachment
                    </span>
                  ) : (
                    ""
                  )}
                </div>
                <div className="flex flex-col w-full">
                  <Label text="New portfolio agreement (English) " />
                  <Form.Item
                    name="newAgreementFile"
                    rules={[
                      {
                        required: true,
                        message: "Please upload the file",
                      },
                    ]}
                  >
                    <Dragger
                      beforeUpload={beforeUpload}
                      customRequest={() => false}
                      multiple={false}
                      accept="image/*,.pdf"
                    >
                      <p className="ant-upload-text">
                        Click or drag file to this area to upload
                      </p>
                    </Dragger>
                  </Form.Item>
                  {portfolioDetails?.newAgreementFile ? (
                    <span
                      className="underline cursor-pointer mb-3 text-blue-500"
                      onClick={() => {
                        setSelectedDocument(portfolioDetails?.newAgreementFile);
                        setOpenPdfViewerModal(true);
                      }}
                    >
                      New portfolio agreement (English) Attachment
                    </span>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <div className="flex justify-between items-center w-full md:w-[48%]">
                <button
                  type="button"
                  onClick={() => navigate("/portfolio/types")}
                  className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-white w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!submitLoginForm}
                  className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                >
                  Update portfolio
                </button>
              </div>
            </div>
          </Form>
        ) : (
          ""
        )}
      </div>

      <CustomModal
        isVisible={openPdfViewerModal}
        centered
        width={1100}
        showClose={false}
        onClose={() => {
          setOpenPdfViewerModal(false);
        }}
        className="edit-modal"
      >
        <div className="flex flex-col items-center justify-center">
          <PdfViewer fileUrl={selectedDocument} />
        </div>
      </CustomModal>
    </section>
  );
};

export default AddEditPortfolio;
